import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useStore } from 'hooks/useStore';
import { generate15MonthPeriods, periodToLabel } from 'data/fdp/oocMockData';
import { OOCTabType, ProbabilityLevel, OOCNDCSearchResult } from '../oocForecast.type';

export { periodToLabel };

export const TAB_CONFIG: { key: OOCTabType; label: string }[] = [
  { key: 'MAS', label: 'MAS DP' },
  { key: 'NC', label: 'NC DP' },
  { key: 'ProvMAS', label: 'Prov. MAS' },
  { key: 'ProvNC', label: 'Prov. NC' },
];

/**
 * Search-result identity key — an NDC can have BOTH a live unconstrained
 * forecast (MAS/NC "DP") and an active provisional forecast (ProvMAS/
 * ProvNC) simultaneously, in which case the backend returns two separate
 * rows for the same ndc11 (one per suggestedTab). Selection state, React
 * keys, and the "add" filter must all key on (ndc11, suggestedTab), not
 * ndc11 alone, or the two rows collapse into one.
 */
export const ndcResultKey = (ndc: Pick<OOCNDCSearchResult, 'ndc11' | 'suggestedTab'>): string =>
  `${ndc.ndc11}::${ndc.suggestedTab}`;

export const WORKFLOW_STEPS = [
  { label: 'Draft', step: 1 },
  { label: 'Submitted / Communicated', step: 2 },
  { label: 'Integrated', step: 3 },
];

export const PROBABILITY_OPTIONS: { label: string; value: ProbabilityLevel }[] = [
  { label: 'Low', value: 'Low' },
  { label: 'Medium', value: 'Medium' },
  { label: 'High', value: 'High' },
];

export const getFcColorClass = (colorType: string, styles: Record<string, string>): string => {
  switch (colorType) {
    case 'new':
      return styles.fcNew;
    case 'modified':
      return styles.fcModified;
    case 'removed':
      return styles.fcRemoved;
    default:
      return styles.fcUnchanged;
  }
};

export const getStatusBadgeClass = (status: string, styles: Record<string, string>): string => {
  switch (status) {
    case 'draft':
      return styles.statusDraft;
    case 'communicated':
      return styles.statusCommunicated;
    case 'integrated':
      return styles.statusIntegrated;
    default:
      return styles.statusDraft;
  }
};

export const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'communicated':
      return 'Submitted / Communicated';
    case 'integrated':
      return 'Integrated';
    default:
      return status;
  }
};

export const useOOCCreateEditLogic = () => {
  const { OOCStore } = useStore();

  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedNDCs, setSelectedNDCs] = useState<Set<string>>(new Set());
  const searchRef = useRef<{ timer: ReturnType<typeof setTimeout> | null; ctrl: AbortController | null }>({ timer: null, ctrl: null });

  const periods = useMemo(() => generate15MonthPeriods(), []);

  const moleculeGroups = useMemo(() => {
    const groups: Record<string, OOCNDCSearchResult[]> = {};
    OOCStore.ndcSearchResults.forEach((ndc) => {
      if (!groups[ndc.molecule]) groups[ndc.molecule] = [];
      groups[ndc.molecule].push(ndc);
    });
    return groups;
  }, [OOCStore.ndcSearchResults]);

  const showProbability = OOCStore.activeTab === 'ProvMAS';

  const handleSearchChange = useCallback((value: string) => {
    OOCStore.setNDCSearchQuery(value);
    setSelectedNDCs(new Set());

    const s = searchRef.current;
    if (s.timer !== null) { clearTimeout(s.timer); s.timer = null; }
    s.ctrl?.abort();
    s.ctrl = null;

    if (value.trim().length < 2) {
      setShowSearchResults(false);
      return;
    }

    s.timer = setTimeout(async () => {
      s.timer = null;
      const ctrl = new AbortController();
      s.ctrl = ctrl;
      try {
        await OOCStore.searchNDCs(ctrl.signal);
        setShowSearchResults(true);
      } catch {
        // Request cancelled by a newer keystroke or failed — do nothing
      }
    }, 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // [] is intentional: OOCStore is a stable singleton, setters are stable

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || OOCStore.ndcSearchQuery.trim().length < 2) return;

    const s = searchRef.current;
    if (s.timer !== null) { clearTimeout(s.timer); s.timer = null; }
    s.ctrl?.abort();
    const ctrl = new AbortController();
    s.ctrl = ctrl;
    OOCStore.searchNDCs(ctrl.signal)
      .then(() => setShowSearchResults(true))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // [] is intentional: same reasoning as handleSearchChange

  const handleToggleNDCCheckbox = useCallback((resultKey: string) => {
    setSelectedNDCs((prev) => {
      const next = new Set(prev);
      if (next.has(resultKey)) next.delete(resultKey);
      else next.add(resultKey);
      return next;
    });
  }, []);

  const handleToggleMoleculeCheckbox = useCallback(
    (molecule: string) => {
      const keysInGroup = (moleculeGroups[molecule] ?? []).map(ndcResultKey);
      setSelectedNDCs((prev) => {
        const next = new Set(prev);
        const allChecked = keysInGroup.every((k) => next.has(k));
        if (allChecked) keysInGroup.forEach((k) => next.delete(k));
        else keysInGroup.forEach((k) => next.add(k));
        return next;
      });
    },
    [moleculeGroups]
  );

  const isMoleculeFullyChecked = useCallback(
    (molecule: string): boolean => {
      const ndcs = moleculeGroups[molecule] ?? [];
      return ndcs.length > 0 && ndcs.every((n) => selectedNDCs.has(ndcResultKey(n)));
    },
    [moleculeGroups, selectedNDCs]
  );

  const isMoleculeIndeterminate = useCallback(
    (molecule: string): boolean => {
      const ndcs = moleculeGroups[molecule] ?? [];
      const count = ndcs.filter((n) => selectedNDCs.has(ndcResultKey(n))).length;
      return count > 0 && count < ndcs.length;
    },
    [moleculeGroups, selectedNDCs]
  );

  const [isAddingNDC, setIsAddingNDC] = useState(false);

  const handleAddNDCButtonClick = useCallback(async () => {
    if (selectedNDCs.size === 0) {
      if (OOCStore.ndcSearchQuery.trim().length >= 2) {
        await OOCStore.searchNDCs();
        setShowSearchResults(true);
      }
      return;
    }

    const ndcsToAdd = OOCStore.ndcSearchResults.filter((n) => selectedNDCs.has(ndcResultKey(n)));
    const ndc11s = ndcsToAdd.map((n) => n.ndc11);
    const firstTab = ndcsToAdd[0]?.suggestedTab;

    // Mark as loading before adding so the skeleton shows immediately
    OOCStore.setLoadingBaseline(ndc11s);
    setIsAddingNDC(true);

    for (const ndc of ndcsToAdd) {
      // Use suggestedTab so MAS→ProvMAS, NC→ProvNC for provisional products
      await OOCStore.addNDCToCurrentSubmission(ndc, ndc.suggestedTab);
      if (ndc.planningType === 'Provisional') {
        // suggestedTab is ProvMAS/ProvNC for provisional products — must be
        // passed through so an NDC with BOTH an active MAS and NC
        // provisional forecast fetches the correct type-specific block
        // instead of colliding on a single ndc11-only store slot. Awaited
        // so the button stays in its loading state until this NDC's
        // provisional grid data is actually ready, not just its adjustment
        // record — matches the other NDCs in the loop below.
        await OOCStore.fetchProvForecastParams(ndc.ndc11, ndc.suggestedTab as 'ProvMAS' | 'ProvNC');
      }
    }

    // Only clear the button's loading state once EVERY selected NDC has
    // been added (and, for provisional ones, had its grid data loaded) —
    // previously this cleared after just the first NDC in the loop, so
    // the button stopped spinning while the rest were still being added.
    setIsAddingNDC(false);

    if (firstTab) OOCStore.setActiveTab(firstTab);

    setSelectedNDCs(new Set());
    OOCStore.setNDCSearchQuery('');
    setShowSearchResults(false);

    // Fetch baseline in background — clears loading state when done
    OOCStore.fetchAndApplyBaseline(ndc11s);
  }, [selectedNDCs, OOCStore]);

  const handleSearchBlur = useCallback(() => {
    setTimeout(() => setShowSearchResults(false), 300);
  }, []);

  const handleSearchFocus = useCallback(() => {
    if (OOCStore.ndcSearchQuery.trim().length >= 2 && OOCStore.ndcSearchResults.length > 0) {
      setShowSearchResults(true);
    }
  }, [OOCStore]);

  // ─── Leave-warning modal ──────────────────────────────────────
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);

  const handleCancelClick = useCallback(() => {
    if (OOCStore.isSubmissionDirty) {
      setShowLeaveWarning(true);
    } else {
      OOCStore.navigateToDashboard();
    }
  }, [OOCStore]);

  const handleConfirmLeave = useCallback(() => {
    setShowLeaveWarning(false);
    OOCStore.navigateToDashboard();
  }, [OOCStore]);

  const handleDismissLeaveWarning = useCallback(() => {
    setShowLeaveWarning(false);
  }, []);

  const handleSaveAndLeave = useCallback(async () => {
    await OOCStore.saveDraft();
    if (!OOCStore.isSubmissionDirty) {
      setShowLeaveWarning(false);
      OOCStore.navigateToDashboard();
    }
  }, [OOCStore]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (OOCStore.isSubmissionDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [OOCStore]);

  // Intercept browser back button
  useEffect(() => {
    window.history.pushState(null, '', window.location.pathname);

    const handlePopState = () => {
      if (OOCStore.isSubmissionDirty) {
        window.history.pushState(null, '', window.location.pathname);
        setShowLeaveWarning(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [OOCStore]);

  return {
    OOCStore,
    periods,
    moleculeGroups,
    showProbability,
    showSearchResults,
    setShowSearchResults,
    selectedNDCs,
    handleSearchChange,
    handleSearchKeyDown,
    handleToggleNDCCheckbox,
    handleToggleMoleculeCheckbox,
    isMoleculeFullyChecked,
    isMoleculeIndeterminate,
    isAddingNDC,
    handleAddNDCButtonClick,
    handleSearchBlur,
    handleSearchFocus,
    showLeaveWarning,
    handleCancelClick,
    handleConfirmLeave,
    handleDismissLeaveWarning,
    handleSaveAndLeave,
  };
};
